import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { SortFieldOption } from 'core/components/server-side-pagination/ServerSideSort'
import Theme from 'core/themes/model'
import DataKeys from 'k8s/DataKeys'
import { humanizeFieldName } from 'utils/misc'
import Progress from '../../components/progress/Progress'
import { Align } from '../tooltip/Tooltip'
import FloatingBatchActionBar from './FloatingBatchActionBar'
import GridEmptyContent from './GridEmptyContent'
import GridHeader from './GridHeader'
import GridPagination from './GridPagination'
import GridRow from './GridRow'
import GridToolbar from './GridToolbar'
import useGridFiltering, { GridFilteringConfig } from './hooks/useGridFiltering'
import useGridManagedColumns, {
  GridManagedColumnSpec,
  GridManagedColumnsConfig,
} from './hooks/useGridManagedColumns'
import useGridPagination, { GridPaginationConfig } from './hooks/useGridPagination'
import useGridRowMenu, { GridRowMenuConfig } from './hooks/useGridRowMenu'
import useGridRows, { Accessor, GridBaseConfig, GridColumnSpec } from './hooks/useGridRows'
import useGridSelectableRows, { GridBatchActionsConfig } from './hooks/useGridSelectableRows'
import useGridSorting, { GridSortableColumnSpec, GridSortingConfig } from './hooks/useGridSorting'

interface GridViewConfig {
  label?: string
  emptyContent?: ReactNode
  extraToolbarContent?: ReactNode
  loading?: boolean
  loadingMessage?: string
  compact?: boolean
  onRefresh?: (...args: unknown[]) => void | Promise<void>
  disableToolbar?: boolean
  ToolbarContainer?: FC<PropsWithChildren<{ className?: string; selectedCount?: number }>>
  showItemsCountInLabel?: boolean
  tooltip?: ReactNode
  align?: Align
  isReadOnly?: boolean
  hideCheckboxes?: boolean
  dataKey?: DataKeys
  footer?: ReactNode
  CustomPaginationComponent?: FC<any>
  useFloatingActionBar?: boolean
  onManageColumnsToggleReady?: (toggle: () => void) => void
  clearSelectionRef?: React.MutableRefObject<(() => void) | null>
  searchComponent?: ReactNode
  hideLastUpdatedTimeStamp?: boolean
}

export type GridViewColumn<T, A extends Accessor<T> = Accessor<T>> = GridColumnSpec<T, A> &
  GridSortableColumnSpec &
  GridManagedColumnSpec<T>

export interface GridProps<
  T,
  GF extends Record<string, unknown> = Record<string, unknown>,
  F extends Record<string, unknown> = Record<string, unknown>,
  C extends GridViewColumn<T> = GridViewColumn<T>,
> extends GridViewConfig,
    GridBaseConfig<T, C>,
    GridSortingConfig<C>,
    GridFilteringConfig<T, GF, F>,
    GridPaginationConfig,
    GridRowMenuConfig<T>,
    GridBatchActionsConfig<T>,
    GridManagedColumnsConfig<T, C> {}

const useStyles = makeStyles<Theme>((theme) => ({
  noTableBorder: {
    borderColor: 'transparent !important',
    borderWidth: '0 !important',
  },
  gridContainer: {
    display: 'grid',
    minWidth: '100%',
    maxWidth: 'max-content',
    backgroundColor: theme.components.table.background,
    borderRadius: 4,
    border: `1px solid ${theme.components.table.border}`,
    boxSizing: 'border-box',
    // so when stretching table vertically, do not stretch toolbar
    gridTemplateRows: 'max-content',
    gridAutoRows: 'max-content',
  },
  grid: {
    minWidth: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    borderSpacing: 0,
    fontWeight: 400,
    lineHeight: 1.42857,
    textRendering: 'optimizeLegibility',
    border: 'none',
    borderBottom: `1px solid ${theme.components.table.border}`,
  },
  gridBody: {
    overflow: 'auto',
  },
  verticalScroll: {
    maxHeight: 'calc(100vh - 240px)',
    overflow: 'auto',
    position: 'relative',
  },
  tr: {
    position: 'relative',
    border: 0,
    height: 56,
    transition: 'background-color 150ms ease',
    borderBottom: `1px solid ${theme.components.table.border}`,
    '&:last-child td': {
      borderBottom: 0,
    },
    '&:hover': {
      backgroundColor: theme.components.table.hoverBackground,
    },
  },
  td: {
    border: 0,
    margin: 0,
    padding: theme.spacing(1),
    ...theme.typography.body2,

    '&:first-child': {
      borderLeft: 'none',
      paddingLeft: 16,
    },
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 16,
    },
    '& > .checkbox': {
      padding: 4,
      marginLeft: -4,
      width: 16,
    },
  },
  cell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  footer: {
    padding: theme.spacing(1.5),
  },
  filterRowContent: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 16,
    padding: '12px 16px',
    alignItems: 'center',
  },
}))

export type GridContextType<T> = {
  triggerRefresh?: () => void
  selectedItems?: T[]
  clearSelectedRows?: () => void
}

export const GridContext = React.createContext({})

/**
 * Convenience shortcut for `useContext<GridContextType<T>>(GridContext)`
 */
export function useGridContext<T>() {
  return useContext<GridContextType<T>>(GridContext)
}

export default function Grid<
  T,
  GF extends Record<string, unknown> = Record<string, unknown>,
  F extends Record<string, unknown> = Record<string, unknown>,
>(configProps: GridProps<T, GF, F>) {
  const classes = useStyles()
  const {
    onRefresh,
    emptyContent = 'No data found',
    disableToolbar = false,
    extraToolbarContent,
    loading = false,
    loadingMessage,
    compact,
    label,
    ToolbarContainer,
    showItemsCountInLabel,
    tooltip,
    align,
    isReadOnly,
    hideCheckboxes,
    dataKey,
    footer,
    CustomPaginationComponent,
    useFloatingActionBar = true,
    onManageColumnsToggleReady,
    clearSelectionRef,
    searchComponent,
    hideLastUpdatedTimeStamp = false,
  } = configProps

  const rows = useGridRows(configProps)
  const [rowsWithActions, rowActionsProps] = useGridRowMenu(rows, configProps)
  const [selectableRows, rowBatchActionsProps] = useGridSelectableRows(rowsWithActions, configProps)
  const [filteredRows, filteringProps] = useGridFiltering(selectableRows, configProps)
  const [sortedRows, sortingProps] = useGridSorting(filteredRows, configProps)
  const [pageRows, paginationProps] = useGridPagination(sortedRows, configProps)
  const [colManagedRows, columnProps] = useGridManagedColumns(pageRows, configProps)

  const toolbarSortOptions = useMemo<SortFieldOption[]>(() => {
    if (configProps.disableSorting || !configProps.columns?.length) return []
    return configProps.columns
      .filter((col) => !col.disableSorting)
      .map((col) => ({
        value: String(col.key),
        label: typeof col.label === 'string' ? col.label : humanizeFieldName(String(col.key)),
      }))
  }, [configProps.columns, configProps.disableSorting])

  const onToolbarSort = useCallback(
    (key: string, dir: 'asc' | 'desc') => {
      if (dir === 'asc') {
        sortingProps.sortByFieldAsc?.(key)
      } else {
        sortingProps.sortByFieldDesc?.(key)
      }
    },
    [sortingProps.sortByFieldAsc, sortingProps.sortByFieldDesc],
  )

  const memoizedOnManageColumnsToggleReady = useMemo(
    () =>
      onManageColumnsToggleReady
        ? (toggle: () => void) => onManageColumnsToggleReady(toggle)
        : undefined,
    [onManageColumnsToggleReady],
  )

  useEffect(() => {
    if (clearSelectionRef) {
      clearSelectionRef.current = rowBatchActionsProps.clearSelectedRows
    }
  }, [clearSelectionRef, rowBatchActionsProps.clearSelectedRows])

  const contextValue = useMemo<GridContextType<T>>(
    () => ({
      triggerRefresh: onRefresh,
      selectedItems: rowBatchActionsProps.selectedItems,
      clearSelectedRows: rowBatchActionsProps.clearSelectedRows,
    }),
    [onRefresh, rowBatchActionsProps.selectedItems, rowBatchActionsProps.clearSelectedRows],
  )

  const floatingBar = useMemo(() => {
    const floatingActionPortalRoot = document.getElementById('floating-action-portal-root')
    if (!useFloatingActionBar || typeof document === 'undefined' || !floatingActionPortalRoot) {
      return null
    }
    return createPortal(
      <FloatingBatchActionBar
        selectedCount={rowBatchActionsProps.selectedCount}
        clearSelectedRows={rowBatchActionsProps.clearSelectedRows}
        batchActions={rowBatchActionsProps.batchActions}
        dropdownBatchActions={rowBatchActionsProps.dropdownBatchActions}
        isReadOnly={isReadOnly}
      />,
      floatingActionPortalRoot,
    )
  }, [
    useFloatingActionBar,
    rowBatchActionsProps.selectedCount,
    rowBatchActionsProps.clearSelectedRows,
    rowBatchActionsProps.batchActions,
    rowBatchActionsProps.dropdownBatchActions,
    isReadOnly,
  ])

  return (
    <Progress overlay loading={loading} message={loadingMessage} inline={compact}>
      <GridContext.Provider value={contextValue}>
        <div
          className={clsx(classes.gridContainer, 'grid-container', {
            [classes.noTableBorder]:
              (!colManagedRows.length && compact) || (compact && disableToolbar),
          })}
        >
          {disableToolbar ? null : (
            <GridToolbar
              ToolbarContainer={ToolbarContainer}
              compact={compact}
              label={label}
              showItemsCountInLabel={showItemsCountInLabel}
              itemsCount={paginationProps?.itemsCount}
              onRefresh={onRefresh}
              extraToolbarContent={extraToolbarContent}
              tooltip={tooltip}
              align={align}
              isReadOnly={isReadOnly}
              dataKey={dataKey}
              useFloatingActionBar={useFloatingActionBar}
              hideLastUpdatedTimeStamp={hideLastUpdatedTimeStamp}
              onManageColumnsToggleReady={memoizedOnManageColumnsToggleReady}
              searchComponent={searchComponent}
              sortOptions={toolbarSortOptions}
              sortedBy={sortingProps.sortedBy as string}
              sortedDirection={sortingProps.sortedDirection}
              onToolbarSort={onToolbarSort}
              {...columnProps}
              {...filteringProps}
              {...rowBatchActionsProps}
            />
          )}
          <div className={clsx(classes.verticalScroll, 'thin-scrollbar')}>
            <section>
              {colManagedRows.length ? (
                <table className={classes.grid} data-testid="grid--table-0">
                  <GridHeader
                    {...columnProps}
                    {...sortingProps}
                    sortingDisabled={sortingProps.sortingDisabled}
                    {...rowBatchActionsProps}
                    rowMenuItemsLength={rowActionsProps.rowMenuItems?.length}
                    pageRows={pageRows}
                    hideCheckboxes={hideCheckboxes}
                    isReadOnly={isReadOnly}
                  />
                  <tbody data-testid="grid--tbody-0">
                    {colManagedRows.map(({ key, ...rowProps }, index) => (
                      <GridRow
                        key={key}
                        className={classes.tr}
                        tdClassName={classes.td}
                        cellClassName={classes.cell}
                        index={index}
                        numPageItems={paginationProps?.currentPageItemsCount}
                        hideCheckboxes={hideCheckboxes}
                        isReadOnly={isReadOnly}
                        {...rowProps}
                        {...rowActionsProps}
                        {...rowBatchActionsProps}
                      />
                    ))}
                  </tbody>
                </table>
              ) : (
                //@ts-ignore
                <GridEmptyContent>{loading ? '' : emptyContent}</GridEmptyContent>
              )}
            </section>
          </div>
          {sortedRows.length && (!compact || sortedRows.length > paginationProps.rowsPerPage) ? (
            CustomPaginationComponent ? (
              <CustomPaginationComponent {...paginationProps} />
            ) : (
              <GridPagination {...paginationProps} />
            )
          ) : null}
          {footer && <div className={classes.footer}>{footer}</div>}
        </div>
        {floatingBar}
      </GridContext.Provider>
    </Progress>
  )
}
